// TODO UPDATE AT REGENTS ADMIN FLIP: RegentsExam.nextAdminDates should contain regents schedules for next admin
// The regents schedule for the upcoming admin should be included in the description of the term flip pivotal card
// if not yet known change all the values to 'Schedule Unknown' (i.e. nextAdminDates:['Schedule'])

// FYI: This file cannot be split up into separate files because of circular dependency
// Regents Exam relies on Regents Category which relies on Regents Subject and Regents Exam
// To split them up we will need to remove category prop from Regents Exam constant
// Big constants should contain little contsants but little constants should not contain big constants

export interface IRegentsExam {
  key: string;
  humanName: string;
  shortName: string;
  humanNameForSsvRegentsPanelTable?: string;
  commonCore: boolean;
  isOffered: boolean;
  order: number;
  scoresToPass: {
    advAndRegents: {
      collegeReady: number;
      pass: number;
    };
    local: {
      collegeReady: number;
      pass: number;
    };
  };
  subject: {
    humanName: string;
    key: string;
    forExemption?: string;
  };
  // if this is empty array that means the exam is NOT offered in the next admin
  // if this is ['Schedule Unknown'] it means that the exam is offered but we don't know the date and time yet
  nextAdminDates: string[]; // 'January 16, 2017, 08:30 EST' OR 'Schedule Unknown'
  category: {
    '5': IRegentsCategory;
    '9': IRegentsCategory;
  };
  discontinuedSiblings: string[];
}

export interface IRegentsSubject {
  key: string;
  humanName: string;
  longName: string;
  // "order" property determines ordering of rows in regents metric table
  order: number;
  exams: IRegentsExam[];
}

export interface IRegentsCategory {
  key: string;
  // "neededOnTrackValue" is mapped to
  // https://github.com/NewVisionsForPublicSchools/nv-student-api/blob/master/models/schemas/studentStatic.js#L485
  neededOnTrackValue: string;
  humanName: string;
  subjects: IRegentsSubject[];
  exams: IRegentsExam[];
  // "order" property determines ordering of boxes in regents panel
  order: number;
  hasCollegeReadiness: boolean;
  byCategory: Array<'5' | '9'>;
  showBox: boolean;
  secondOrThirdCat: boolean;
}

// DO NOT CHANGE SHORT NAME VALUE - IT IS USED TO MAP to regentsDetails.byExam[examKey].exam
export const RegentsExam = {
  COMP_ELA: {
    key: 'compEla',
    humanName: 'Comp ELA',
    shortName: 'Comp ELA',
    commonCore: false,
    isOffered: false,
    order: 1,
    scoresToPass: {
      advAndRegents: {
        collegeReady: 75,
        pass: 65,
      },
      local: {
        collegeReady: 80,
        pass: 55,
      },
    },
    subject: {
      humanName: 'ELA',
      key: 'ela',
    },
    nextAdminDates: [],
    discontinuedSiblings: [],
  } as IRegentsExam,
  CC_ELA: {
    key: 'ccEla',
    humanName: 'CC ELA',
    shortName: 'CC ELA',
    humanNameForSsvRegentsPanelTable: 'ELA',
    commonCore: true,
    isOffered: true,
    order: 2,
    scoresToPass: {
      advAndRegents: {
        collegeReady: 75,
        pass: 65,
      },
      local: {
        collegeReady: 75,
        pass: 55,
      },
    },
    subject: {
      humanName: 'ELA',
      key: 'ela',
      forExemption: 'ELA_CORE',
    },
    nextAdminDates: ['Schedule Unknown'],
    discontinuedSiblings: ['compEla'],
  } as IRegentsExam,
  INT_ALG: {
    key: 'intAlg',
    humanName: 'Algebra',
    shortName: 'Int Alg',
    commonCore: false,
    isOffered: false,
    order: 3,
    scoresToPass: {
      advAndRegents: {
        collegeReady: 80,
        pass: 65,
      },
      local: {
        collegeReady: 80,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Alg',
      key: 'alg',
    },
    nextAdminDates: [],
    discontinuedSiblings: [],
  } as IRegentsExam,
  CC_ALG: {
    key: 'ccAlg',
    humanName: 'CC Algebra',
    shortName: 'CC Alg',
    humanNameForSsvRegentsPanelTable: 'Algebra',
    commonCore: true,
    isOffered: true,
    order: 4,
    scoresToPass: {
      advAndRegents: {
        collegeReady: 70,
        pass: 65,
      },
      local: {
        collegeReady: 70,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Alg',
      key: 'alg',
      forExemption: 'MATH_ALG',
    },
    discontinuedSiblings: ['intAlg'],
    nextAdminDates: ['Schedule Unknown'],
  } as IRegentsExam,
  OLD_GEOM: {
    key: 'oldGeom',
    humanName: 'Geometry',
    shortName: 'Geom',
    commonCore: false,
    isOffered: false,
    order: 5,
    scoresToPass: {
      advAndRegents: {
        collegeReady: 80,
        pass: 65,
      },
      local: {
        collegeReady: 80,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Geom',
      key: 'geom',
    },
    nextAdminDates: [],
    discontinuedSiblings: [],
  } as IRegentsExam,
  CC_GEOM: {
    key: 'ccGeom',
    humanName: 'CC Geometry',
    shortName: 'CC Geom',
    humanNameForSsvRegentsPanelTable: 'Geometry',
    commonCore: true,
    isOffered: true,
    order: 6,
    scoresToPass: {
      advAndRegents: {
        collegeReady: 70,
        pass: 65,
      },
      local: {
        collegeReady: 70,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Geom',
      key: 'geom',
      forExemption: 'MATH_GEOM',
    },
    discontinuedSiblings: ['oldGeom'],
    nextAdminDates: ['Schedule Unknown'],
  } as IRegentsExam,
  OLD_TRIG: {
    key: 'oldTrig',
    humanName: 'Trigonometry',
    shortName: 'Trig',
    commonCore: false,
    isOffered: false,
    order: 7,
    scoresToPass: {
      advAndRegents: {
        collegeReady: 80,
        pass: 65,
      },
      local: {
        collegeReady: 80,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Trig',
      key: 'trig',
    },
    nextAdminDates: [],
    discontinuedSiblings: [],
  } as IRegentsExam,
  CC_TRIG: {
    key: 'ccTrig',
    humanName: 'CC Trigonometry',
    shortName: 'CC Trig',
    humanNameForSsvRegentsPanelTable: 'Trigonometry',
    commonCore: true,
    isOffered: true,
    order: 8,
    scoresToPass: {
      advAndRegents: {
        collegeReady: 65,
        pass: 65,
      },
      local: {
        collegeReady: 65,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Trig',
      key: 'trig',
      forExemption: 'MATH_TRIG',
    },
    discontinuedSiblings: ['oldTrig'],
    nextAdminDates: ['Schedule Unknown'],
  } as IRegentsExam,
  LIVING_ENV: {
    key: 'livingEnv',
    humanName: 'Living Env',
    shortName: 'Liv Env',
    commonCore: false,
    isOffered: true,
    order: 9,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Liv Env',
      key: 'livingEnv',
      forExemption: 'SCI_LIVING',
    },
    discontinuedSiblings: [],
    nextAdminDates: ['Schedule Unknown'],
  } as IRegentsExam,
  EARTH_SCI: {
    key: 'earth',
    humanName: 'Earth Science',
    shortName: 'Earth',
    commonCore: false,
    isOffered: true,
    order: 10,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Earth',
      key: 'earth',
      forExemption: 'SCI_EARTH',
    },
    discontinuedSiblings: [],
    nextAdminDates: ['Schedule Unknown'],
  } as IRegentsExam,
  CHEM: {
    key: 'chem',
    humanName: 'Chemistry',
    shortName: 'Chem',
    commonCore: false,
    isOffered: true,
    order: 11,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Chem',
      key: 'chem',
      forExemption: 'SCI_CHEM',
    },
    discontinuedSiblings: [],
    nextAdminDates: ['Schedule Unknown'],
  } as IRegentsExam,
  PHYSICS: {
    key: 'physics',
    humanName: 'Physics',
    shortName: 'Physics',
    commonCore: false,
    isOffered: true,
    order: 12,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Physics',
      key: 'physics',
      forExemption: 'SCI_PHYSICS',
    },
    discontinuedSiblings: [],
    nextAdminDates: ['Schedule Unknown'],
  } as IRegentsExam,
  GLOBAL: {
    key: 'global',
    humanName: 'Global',
    shortName: 'Global',
    commonCore: false,
    isOffered: true,
    order: 13,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Global',
      key: 'global',
      forExemption: 'SS_GLOBAL',
    },
    nextAdminDates: ['Schedule Unknown'],
    discontinuedSiblings: [],
  } as IRegentsExam,
  GLOBAL_TWO: {
    key: 'globalTwo',
    humanName: 'Global II',
    shortName: 'Global II',
    commonCore: true,
    isOffered: true,
    order: 14,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'Global',
      key: 'global',
      forExemption: 'SS_GLOBAL',
    },
    discontinuedSiblings: [],
    nextAdminDates: ['Schedule Unknown'],
  } as IRegentsExam,
  US: {
    key: 'us',
    humanName: 'US',
    shortName: 'US',
    commonCore: false,
    isOffered: true,
    order: 15,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'US',
      key: 'us',
      forExemption: 'SS_US',
    },
    nextAdminDates: ['Schedule Unknown'],
    discontinuedSiblings: [],
  } as IRegentsExam,
  US_FRAMEWORK: {
    key: 'usFramework',
    humanName: 'US Framework',
    shortName: 'US (Framework)',
    commonCore: true,
    isOffered: true,
    order: 16,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'US',
      key: 'us',
      forExemption: 'SS_US',
    },
    nextAdminDates: ['Schedule Unknown'],
    discontinuedSiblings: [],
  } as IRegentsExam,
  LOTE: {
    key: 'lote',
    humanName: 'LOTE',
    shortName: 'LOTE',
    commonCore: false,
    isOffered: true,
    order: 17,
    scoresToPass: {
      advAndRegents: {
        collegeReady: null,
        pass: 65,
      },
      local: {
        collegeReady: null,
        pass: 55,
      },
    },
    subject: {
      humanName: 'LOTE',
      key: 'lote',
      forExemption: 'LOTE',
    },
    nextAdminDates: ['Schedule Unknown'],
    discontinuedSiblings: [],
  } as IRegentsExam,
};

export const RegentsSubject = {
  ELA: {
    key: 'ela',
    humanName: 'ELA',
    longName: 'ELA',
    order: 1,
    exams: [RegentsExam.COMP_ELA, RegentsExam.CC_ELA],
  } as IRegentsSubject,
  ALGEBRA: {
    key: 'alg',
    humanName: 'Alg',
    longName: 'Algebra',
    order: 2,
    exams: [RegentsExam.INT_ALG, RegentsExam.CC_ALG],
  } as IRegentsSubject,
  GEOMETRY: {
    key: 'geom',
    humanName: 'Geom',
    longName: 'Geometry',
    order: 3,
    exams: [RegentsExam.OLD_GEOM, RegentsExam.CC_GEOM],
  } as IRegentsSubject,
  TRIG: {
    key: 'trig',
    humanName: 'Trig',
    longName: 'Trigonometry',
    order: 4,
    exams: [RegentsExam.OLD_TRIG, RegentsExam.CC_TRIG],
  } as IRegentsSubject,
  LIVING_ENV: {
    key: 'livingEnv',
    humanName: 'Liv Env',
    longName: 'Living Environment',
    order: 5,
    exams: [RegentsExam.LIVING_ENV],
  } as IRegentsSubject,
  EARTH: {
    key: 'earth',
    humanName: 'Earth',
    longName: 'Earth Science',
    order: 6,
    exams: [RegentsExam.EARTH_SCI],
  } as IRegentsSubject,
  CHEMISTRY: {
    key: 'chem',
    humanName: 'Chem',
    longName: 'Chemistry',
    order: 7,
    exams: [RegentsExam.CHEM],
  } as IRegentsSubject,
  PHYSICS: {
    key: 'physics',
    humanName: 'Physics',
    longName: 'Physics',
    order: 8,
    exams: [RegentsExam.PHYSICS],
  } as IRegentsSubject,
  GLOBAL: {
    key: 'global',
    humanName: 'Global',
    longName: 'Global',
    order: 9,
    exams: [RegentsExam.GLOBAL, RegentsExam.GLOBAL_TWO],
  } as IRegentsSubject,
  US: {
    key: 'us',
    humanName: 'US',
    longName: 'US',
    order: 10,
    exams: [RegentsExam.US, RegentsExam.US_FRAMEWORK],
  } as IRegentsSubject,
  LOTE: {
    key: 'lote',
    humanName: 'LOTE',
    longName: 'LOTE',
    order: 11,
    exams: [RegentsExam.LOTE],
  } as IRegentsSubject,
};

export const RegentsCategory = {
  ELA: {
    key: 'ela',
    neededOnTrackValue: 'ELA',
    humanName: 'ELA',
    subjects: [RegentsSubject.ELA],
    exams: RegentsSubject.ELA.exams,
    order: 1,
    hasCollegeReadiness: true,
    byCategory: ['5', '9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  GLOBAL: {
    key: 'global',
    neededOnTrackValue: 'Global',
    humanName: 'Global',
    subjects: [RegentsSubject.GLOBAL],
    exams: RegentsSubject.GLOBAL.exams,
    order: 4,
    hasCollegeReadiness: false,
    byCategory: ['9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  US: {
    key: 'us',
    neededOnTrackValue: 'US',
    humanName: 'US',
    subjects: [RegentsSubject.US],
    exams: RegentsSubject.US.exams,
    order: 5,
    hasCollegeReadiness: false,
    byCategory: ['9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  ALGEBRA: {
    key: 'alg',
    neededOnTrackValue: 'Alg',
    humanName: 'Algebra',
    subjects: [RegentsSubject.ALGEBRA],
    exams: RegentsSubject.ALGEBRA.exams,
    order: 2,
    hasCollegeReadiness: true,
    byCategory: ['9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  GEOMETRY: {
    key: 'geom',
    neededOnTrackValue: 'Geom',
    humanName: 'Geom',
    subjects: [RegentsSubject.GEOMETRY],
    exams: RegentsSubject.GEOMETRY.exams,
    order: 7,
    hasCollegeReadiness: true,
    byCategory: ['9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  TRIGONOMETRY: {
    key: 'trig',
    neededOnTrackValue: 'Trig',
    humanName: 'Trig',
    subjects: [RegentsSubject.TRIG],
    exams: RegentsSubject.TRIG.exams,
    order: 8,
    hasCollegeReadiness: true,
    byCategory: ['9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  LIFE_SCIENCE: {
    key: 'lifeSci',
    neededOnTrackValue: 'Living',
    humanName: 'Life Sci',
    subjects: [RegentsSubject.LIVING_ENV],
    exams: RegentsSubject.LIVING_ENV.exams,
    order: 3,
    hasCollegeReadiness: false,
    byCategory: ['9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  PHYSICAL_SCIENCE: {
    key: 'physSci',
    neededOnTrackValue: 'Physical',
    humanName: 'Phys Sci',
    subjects: [RegentsSubject.EARTH, RegentsSubject.CHEMISTRY, RegentsSubject.PHYSICS],
    exams: RegentsSubject.EARTH.exams.concat(RegentsSubject.CHEMISTRY.exams).concat(RegentsSubject.PHYSICS.exams),
    order: 6,
    hasCollegeReadiness: false,
    byCategory: ['9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  LOTE: {
    key: 'lote',
    neededOnTrackValue: 'LOTE',
    humanName: 'LOTE',
    subjects: [RegentsSubject.LOTE],
    exams: RegentsSubject.LOTE.exams,
    order: 10,
    hasCollegeReadiness: false,
    byCategory: ['9'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  MATH: {
    key: 'math',
    neededOnTrackValue: 'Math',
    humanName: 'Math',
    subjects: [RegentsSubject.ALGEBRA, RegentsSubject.GEOMETRY, RegentsSubject.TRIG],
    exams: RegentsSubject.ALGEBRA.exams.concat(RegentsSubject.GEOMETRY.exams).concat(RegentsSubject.TRIG.exams),
    order: 2,
    hasCollegeReadiness: true,
    byCategory: ['5'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  SCIENCE: {
    key: 'sci',
    neededOnTrackValue: 'Sci',
    humanName: 'Sci',
    subjects: [RegentsSubject.LIVING_ENV, RegentsSubject.CHEMISTRY, RegentsSubject.PHYSICS, RegentsSubject.EARTH],
    exams: RegentsSubject.LIVING_ENV.exams
      .concat(RegentsSubject.CHEMISTRY.exams)
      .concat(RegentsSubject.PHYSICS.exams)
      .concat(RegentsSubject.EARTH.exams),
    order: 3,
    hasCollegeReadiness: false,
    byCategory: ['5'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  SOCIAL_STUDIES: {
    key: 'ss',
    neededOnTrackValue: 'SS',
    humanName: 'SS',
    subjects: [RegentsSubject.US, RegentsSubject.GLOBAL],
    exams: RegentsSubject.US.exams.concat(RegentsSubject.GLOBAL.exams),
    order: 4,
    hasCollegeReadiness: false,
    byCategory: ['5'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  PLUS_1: {
    key: 'plus1',
    neededOnTrackValue: 'Plus One',
    humanName: 'Plus 1',
    subjects: [
      RegentsSubject.ALGEBRA,
      RegentsSubject.GEOMETRY,
      RegentsSubject.TRIG,
      RegentsSubject.LIVING_ENV,
      RegentsSubject.CHEMISTRY,
      RegentsSubject.PHYSICS,
      RegentsSubject.EARTH,
      RegentsSubject.US,
      RegentsSubject.GLOBAL,
    ],
    exams: RegentsSubject.ALGEBRA.exams
      .concat(RegentsSubject.GEOMETRY.exams)
      .concat(RegentsSubject.TRIG.exams)
      .concat(RegentsSubject.LIVING_ENV.exams)
      .concat(RegentsSubject.CHEMISTRY.exams)
      .concat(RegentsSubject.PHYSICS.exams)
      .concat(RegentsSubject.EARTH.exams)
      .concat(RegentsSubject.US.exams)
      .concat(RegentsSubject.GLOBAL.exams),
    order: 5,
    hasCollegeReadiness: false,
    byCategory: ['5'],
    showBox: true,
    secondOrThirdCat: false,
  } as IRegentsCategory,
  SECOND_SS: {
    key: 'secondSs',
    neededOnTrackValue: null,
    humanName: '2nd SS',
    subjects: [RegentsSubject.US, RegentsSubject.GLOBAL],
    exams: RegentsSubject.US.exams.concat(RegentsSubject.GLOBAL.exams),
    order: 6,
    hasCollegeReadiness: false,
    byCategory: ['5'],
    showBox: false,
    secondOrThirdCat: true,
  } as IRegentsCategory,
  SECOND_SCI: {
    key: 'secondSci',
    neededOnTrackValue: null,
    humanName: '2nd Sci',
    subjects: [RegentsSubject.LIVING_ENV, RegentsSubject.CHEMISTRY, RegentsSubject.PHYSICS, RegentsSubject.EARTH],
    exams: RegentsSubject.LIVING_ENV.exams
      .concat(RegentsSubject.CHEMISTRY.exams)
      .concat(RegentsSubject.PHYSICS.exams)
      .concat(RegentsSubject.EARTH.exams),
    order: 7,
    hasCollegeReadiness: false,
    byCategory: ['5'],
    showBox: false,
    secondOrThirdCat: true,
  } as IRegentsCategory,
  SECOND_MATH: {
    key: 'secondMath',
    neededOnTrackValue: null,
    humanName: '2nd Math',
    subjects: [RegentsSubject.ALGEBRA, RegentsSubject.GEOMETRY, RegentsSubject.TRIG],
    exams: RegentsSubject.ALGEBRA.exams.concat(RegentsSubject.GEOMETRY.exams).concat(RegentsSubject.TRIG.exams),
    order: 8,
    hasCollegeReadiness: true,
    byCategory: ['5'],
    showBox: false,
    secondOrThirdCat: true,
  } as IRegentsCategory,
  THIRD_MATH: {
    key: 'thirdMath',
    neededOnTrackValue: null,
    humanName: '3rd Math',
    subjects: [RegentsSubject.ALGEBRA, RegentsSubject.GEOMETRY, RegentsSubject.TRIG],
    exams: RegentsSubject.ALGEBRA.exams.concat(RegentsSubject.GEOMETRY.exams).concat(RegentsSubject.TRIG.exams),
    order: 9,
    hasCollegeReadiness: true,
    byCategory: ['5'],
    showBox: false,
    secondOrThirdCat: true,
  } as IRegentsCategory,
};

RegentsExam.COMP_ELA.category = {
  5: RegentsCategory.ELA,
  9: RegentsCategory.ELA,
};

RegentsExam.CC_ELA.category = {
  5: RegentsCategory.ELA,
  9: RegentsCategory.ELA,
};

RegentsExam.INT_ALG.category = {
  5: RegentsCategory.MATH,
  9: RegentsCategory.ALGEBRA,
};

RegentsExam.CC_ALG.category = {
  5: RegentsCategory.MATH,
  9: RegentsCategory.ALGEBRA,
};

RegentsExam.OLD_GEOM.category = {
  5: RegentsCategory.MATH,
  9: RegentsCategory.GEOMETRY,
};

RegentsExam.CC_GEOM.category = {
  5: RegentsCategory.MATH,
  9: RegentsCategory.GEOMETRY,
};

RegentsExam.OLD_TRIG.category = {
  5: RegentsCategory.MATH,
  9: RegentsCategory.TRIGONOMETRY,
};

RegentsExam.CC_TRIG.category = {
  5: RegentsCategory.MATH,
  9: RegentsCategory.TRIGONOMETRY,
};

RegentsExam.LIVING_ENV.category = {
  5: RegentsCategory.SCIENCE,
  9: RegentsCategory.LIFE_SCIENCE,
};

RegentsExam.EARTH_SCI.category = {
  5: RegentsCategory.SCIENCE,
  9: RegentsCategory.PHYSICAL_SCIENCE,
};

RegentsExam.CHEM.category = {
  5: RegentsCategory.SCIENCE,
  9: RegentsCategory.PHYSICAL_SCIENCE,
};

RegentsExam.PHYSICS.category = {
  5: RegentsCategory.SCIENCE,
  9: RegentsCategory.PHYSICAL_SCIENCE,
};

RegentsExam.GLOBAL.category = {
  5: RegentsCategory.SOCIAL_STUDIES,
  9: RegentsCategory.GLOBAL,
};

RegentsExam.GLOBAL_TWO.category = {
  5: RegentsCategory.SOCIAL_STUDIES,
  9: RegentsCategory.GLOBAL,
};

RegentsExam.US.category = {
  5: RegentsCategory.SOCIAL_STUDIES,
  9: RegentsCategory.US,
};

RegentsExam.US_FRAMEWORK.category = {
  5: RegentsCategory.SOCIAL_STUDIES,
  9: RegentsCategory.US,
};

RegentsExam.LOTE.category = {
  5: RegentsCategory.LOTE,
  9: RegentsCategory.LOTE,
};
